import { useNavigate } from 'react-router-dom';
import { Button } from '../components/common/Button';
import { Logo } from '../components/common/Logo';
import { PairForm, PairFeedback } from '../components/pair/PairForm';
import { PairedDevices } from '../components/pair/PairedDevices';
import { useAuthStore } from '../stores/authStore';
import { useHeadsetDevices } from '../hooks/useHeadsetDevices';
import { usePairingActions } from '../components/settings/headsetPairingState';

/**
 * Dedicated headset pairing page at /pair.
 */
export default function PairPage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const devices = useHeadsetDevices(user?.uid);
  const { state, actions } = usePairingActions();
  const isBusy = state.status === 'loading';

  return (
    <div className="min-h-screen flex flex-col">
      <PairHeader onHome={() => navigate('/')} />
      <main className="flex-1 flex items-start justify-center px-6 py-12">
        <div className="w-full max-w-lg space-y-6">
          <PairHero />
          <PairForm
            code={state.code}
            deviceName={state.deviceName}
            isBusy={isBusy}
            onChangeCode={actions.setCode}
            onChangeDeviceName={actions.setDeviceName}
            onPair={actions.handlePair}
          />
          <PairFeedback status={state.status} message={state.message} />
          <PairedDevices devices={devices} onUnpair={actions.handleUnpair} isBusy={isBusy} />
        </div>
      </main>
    </div>
  );
}

interface PairHeaderProps {
  onHome: () => void;
}

/**
 * Minimal header with logo and home navigation.
 */
function PairHeader({ onHome }: PairHeaderProps) {
  return (
    <header className="glass border-b border-white/10">
      <div className="max-w-2xl mx-auto px-6 py-4 flex items-center justify-between">
        <Logo size="sm" />
        <Button variant="secondary" size="sm" onClick={onHome}>
          Home
        </Button>
      </div>
    </header>
  );
}

/**
 * Hero section with pairing instructions.
 */
function PairHero() {
  return (
    <div className="text-center space-y-2">
      <h1 className="text-3xl font-display font-bold text-white">
        Pair Your Headset
      </h1>
      <p className="text-ink-200 text-base">
        Open VR Meet on your Quest and enter the 4-character code shown in the headset.
      </p>
    </div>
  );
}
